import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import useConfigQuery from "hooks/query/useConfigQuery";
import useQueryString from "hooks/useQueryString";
import { appInsights, reactPlugin, setAppInsightsConnectionString, } from "libs/app-insights";
import { languages, TranslationKey } from "libs/locale/i18nLanguages";
import { lazy, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ReactQueryDevtools } from "react-query/devtools";
import { Route, Routes } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { coldStartContextState } from "states/app";
import { msTeamsContextQuery } from "states/ms-teams";

const AuthStart = lazy(() => import("components/pages/AuthStart"));
const AuthEnd = lazy(() => import("components/pages/AuthEnd"));
const AppLinksTab = lazy(() => import("components/pages/AppLinksTab"));
const NotFound = lazy(() => import("components/pages/NotFound"));

export const AuthStartPath = "/auth-start";

export const AuthEndPath = "/auth-end";

export const AppLinksTabPath = "/tab";

export default function App() {
  const config = useConfigQuery();
  const connectionString = config?.appInsightsConnectionString;
  const microsoftTeamsContext = useRecoilValue(msTeamsContextQuery);

  const { i18n } = useTranslation();
  const queryString = useQueryString();

  // 팀즈에서 로케일 값을 "ko-kr"로 주기 때문에 언어 부분만 분리하여 사용하도록 합니다.
  const queryStringLocale = queryString.get("locale");
  const teamsLocale = microsoftTeamsContext.app.locale.split("-")[0];
  const upn = microsoftTeamsContext.user?.userPrincipalName;

  useEffect(() => {
    // 고객이 다국어 기능을 요청하기 전까지는 의도적으로 기능을 숨기고 있도록 합니다.
    if (!config.enableMultilingualFunction) return;

    const locale = queryStringLocale ?? teamsLocale ?? languages.ko;
    console.log("Changing language to:", locale);

    i18n.changeLanguage(locale)
      .then(() => {
        appInsights.trackTrace({
          message: `${upn} 사용자 ${locale} 다국어 적용`,
        });
      });
  }, [queryStringLocale, teamsLocale, config.enableMultilingualFunction, i18n, upn]);

    // App Insights 초기화는 설정 수신 후 1회만 시도합니다.
    useEffect(() => {
        if (connectionString) setAppInsightsConnectionString(connectionString);
    }, [connectionString]);

  useSetRecoilState(coldStartContextState)(TranslationKey["페이지 불러오는 중..."]);

  return (
    <>
      <AppInsightsContext.Provider value={reactPlugin}>
        <Routes>
          <Route path={AuthStartPath} element={<AuthStart />} />
          <Route path={AuthEndPath} element={<AuthEnd />} />
          <Route path={AppLinksTabPath} element={<AppLinksTab />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AppInsightsContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
}
